/**
 * Whose name this address wears (Phase 4 foundation, 2026-10-29). The main app is CAIOS; a partner's portal
 * (portal.liveecho.com…) is that partner — its name on the sign-in and invitation pages, in the tab, and its logo in
 * the rail. Asked once per page load; until it answers, nothing names anyone (never a flash of "CAIOS" on a partner).
 */
import { useEffect, useState } from 'react';

export interface Brand { name: string; logoUrl: string | null; accent: string | null; portal: boolean }
let once: Promise<Brand | null> | undefined;
const load = () => (once ??= fetch('/api/v1/brand').then((r) => (r.ok ? (r.json() as Promise<Brand>) : null)).catch(() => null));

/** Dark text on a light accent, white on a dark one (WCAG relative luminance). */
function onAccent(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b! > 0.4 ? '#1A1408' : '#FFFFFF';
}
/** A partner's colour on every accent, and its logo as the tab and home-screen icon (the CAIOS mark is never shown). */
function wear(x: Brand) {
  const root = document.documentElement;
  if (x.accent && /^#[0-9a-f]{6}$/i.test(x.accent)) {
    root.style.setProperty('--cx-brand-accent', x.accent);
    root.style.setProperty('--cx-brand-on-accent', onAccent(x.accent));
    root.dataset.brand = '';
  }
  document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach((l) => l.remove());
  if (x.logoUrl) for (const rel of ['icon', 'apple-touch-icon']) {
    const l = document.createElement('link'); l.rel = rel; l.href = x.logoUrl; document.head.append(l);
  }
}

export function useBrand(): Brand | null {
  const [b, setB] = useState<Brand | null>(null);
  useEffect(() => {
    let live = true;
    void load().then((x) => {
      if (!live || !x) return;
      setB(x);
      document.title = x.name;
      if (x.portal) wear(x);
    });
    return () => { live = false; };
  }, []);
  return b;
}
